import { useEffect, useState, RefObject, useRef } from 'react'

type UseInViewOptions = {
  rootMargin?: string
  threshold?: number | number[]
  triggerOnce?: boolean
  onIntersect?: () => void
}

export const useInView = <T extends HTMLElement = HTMLDivElement>(
  ref: RefObject<T>,
  options: UseInViewOptions = {}
): boolean => {
  const {
    rootMargin = '0px',
    threshold = 0,
    triggerOnce = false,
    onIntersect
  } = options

  const [isInView, setIsInView] = useState(false)
  const [hasTriggered, setHasTriggered] = useState(false)
  const onIntersectRef = useRef(onIntersect)

  // Keep the callback ref up to date
  useEffect(() => {
    onIntersectRef.current = onIntersect
  }, [onIntersect])

  useEffect(() => {
    const element = ref.current
    if (!element) return

    // If triggerOnce is true and we've already triggered, don't set up observer
    if (triggerOnce && hasTriggered) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true)
            
            if (triggerOnce && !hasTriggered) {
              setHasTriggered(true)
              onIntersectRef.current?.()
              observer.disconnect()
            } else if (!triggerOnce) {
              onIntersectRef.current?.()
            }
          } else if (!triggerOnce) {
            setIsInView(false)
          }
        })
      },
      {
        rootMargin,
        threshold
      }
    )

    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [ref, rootMargin, threshold, triggerOnce, hasTriggered])

  return isInView
}

